/**
 * Generic PawnComponent
 *
 * Renders a soccer ball pawn piece with dynamic color scheme.
 * Features a 3D soccer ball design with configurable colors,
 * metallic gray ball top, shadow effects, and pentagon pattern details.
 *
 * This is the base component used by color-specific wrappers (RedPawn, BluePawn, etc.)
 */

import type { PawnProps } from '../../constants/types';
import type { PawnColorScheme, PawnGradientConfig } from './constants/pawnColorSchemes';
import { buildClassNames } from '@/utils/classNames';

interface PawnComponentProps extends PawnProps {
  colorScheme: PawnColorScheme;
}

function renderGradient(gradient: PawnGradientConfig) {
  return (
    <linearGradient
      id={gradient.id}
      x1={gradient.x1}
      y1={gradient.y1}
      x2={gradient.x2}
      y2={gradient.y2}
      gradientUnits="userSpaceOnUse"
    >
      {gradient.stops.map((stop, index) => (
        <stop
          key={index}
          offset={stop.offset}
          stopColor={stop.stopColor}
          stopOpacity={stop.stopOpacity}
        />
      ))}
    </linearGradient>
  );
}

export default function PawnComponent({
  colorScheme,
  viewBox = '0 0 323 473',
  className = '',
  isActive = false,
  isHovered = false,
  isSelected = false,
  isMoveable = false
}: PawnComponentProps) {
  const svgClassName = buildClassNames(
    className,
    { condition: isActive, className: 'pawn-active' },
    { condition: isHovered, className: 'pawn-hovered' },
    { condition: isSelected, className: 'pawn-selected' },
    { condition: isMoveable, className: 'pawn-moveable' }
  );

  const { gradients, solidColors } = colorScheme;

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={colorScheme.ariaLabel}
      focusable="false"
    >
      <defs>
        {renderGradient(gradients.shadow1)}
        {renderGradient(gradients.base1)}
        {renderGradient(gradients.base2)}
        {renderGradient(gradients.ball1)}
        {renderGradient(gradients.ball2)}
        {renderGradient(gradients.detail1)}
        {renderGradient(gradients.detail2)}
      </defs>

      {/* Main pawn shape - shadow base */}
      <path
        d="M161.12 430.6C97.5002 430.6 45.7402 382.58 45.7402 323.55C45.7402 308.95 48.8502 294.79 54.9902 281.47C57.4302 260.49 65.8202 241.01 79.3102 224.92C78.2102 223.71 77.1302 222.46 76.0502 221.14C65.1702 207.91 57.6102 192.11 54.1802 175.43C52.4902 167.33 51.7702 158.94 52.0502 150.5C52.6002 133.91 56.8602 118.14 64.7402 103.63C72.3402 89.5999 83.2402 77.26 96.2702 67.94C114.29 55.03 135.54 48.2 157.72 48.2C158.9 48.2 160.08 48.2199 161.26 48.2599C168.3 48.4999 175.2 49.4 181.76 50.95C187.96 52.39 194.06 54.3999 199.88 56.9199C205.5 59.3499 211.05 62.37 216.36 65.9C226.34 72.53 235.06 80.7599 242.28 90.3399C249.57 100.01 255.1 110.75 258.71 122.27L258.85 122.71V122.73C259.35 124.31 259.82 125.97 260.28 127.78C262.45 136.3 263.55 145.09 263.55 153.92C263.55 155.09 263.53 156.27 263.49 157.46C262.96 173.64 258.89 189.05 251.38 203.27C249.51 206.83 247.39 210.34 245.08 213.69C242.99 216.74 240.74 219.68 238.37 222.45C251.49 237.18 260.25 255.15 263.78 274.61C272.1 289.64 276.49 306.54 276.49 323.53C276.49 382.56 224.73 430.58 161.11 430.58L161.12 430.6Z"
        fill={`url(#${gradients.shadow1.id})`}
      />

      {/* Base bottom - gradient */}
      <path
        d="M161.12 427.61C223.186 427.61 273.5 381.016 273.5 323.54C273.5 266.064 223.186 219.47 161.12 219.47C99.0545 219.47 48.7402 266.064 48.7402 323.54C48.7402 381.016 99.0545 427.61 161.12 427.61Z"
        fill={`url(#${gradients.base1.id})`}
      />

      {/* Base mid-section - dark solid */}
      <path
        d="M159.9 408.12C216.586 408.12 262.54 361.526 262.54 304.05C262.54 246.574 216.586 199.98 159.9 199.98C103.213 199.98 57.2598 246.574 57.2598 304.05C57.2598 361.526 103.213 408.12 159.9 408.12Z"
        fill={solidColors.baseMidSection}
      />

      {/* Base upper section - depth gradient */}
      <path
        d="M159.9 398.23C216.586 398.23 262.54 351.636 262.54 294.16C262.54 236.684 216.586 190.09 159.9 190.09C103.213 190.09 57.2598 236.684 57.2598 294.16C57.2598 351.636 103.213 398.23 159.9 398.23Z"
        fill={`url(#${gradients.base2.id})`}
      />

      {/* Ball connection - light solid */}
      <path
        d="M161.48 354.46C122.79 354.46 91.3198 322.35 91.3198 282.88C91.3198 243.41 122.79 211.3 161.48 211.3C200.17 211.3 231.64 243.41 231.64 282.88C231.64 322.35 200.17 354.46 161.48 354.46Z"
        fill={solidColors.ballConnection}
      />

      {/* Ball separator - black ring */}
      <path
        d="M161.12 352.46C201.387 352.46 234.03 319.365 234.03 278.54C234.03 237.715 201.387 204.62 161.12 204.62C120.853 204.62 88.21 237.715 88.21 278.54C88.21 319.365 120.853 352.46 161.12 352.46Z"
        fill={solidColors.ballSeparator}
      />

      {/* Ball shine effect */}
      <path
        opacity="0.57"
        d="M161.12 348.4C201.387 348.4 234.03 315.305 234.03 274.48C234.03 233.655 201.387 200.56 161.12 200.56C120.853 200.56 88.21 233.655 88.21 274.48C88.21 315.305 120.853 348.4 161.12 348.4Z"
        fill={`url(#${gradients.ball1.id})`}
      />

      {/* Neck section - light solid */}
      <path
        d="M81.8498 210.88L114.82 301.75C121.47 320.08 138.6 332.51 158.09 333.15C180.01 333.87 199.6 319.55 205.56 298.44L230.88 208.76C239.36 178.71 217.17 148.76 185.95 148.12L127.67 146.93C94.1798 146.24 70.4298 179.39 81.8598 210.88H81.8498Z"
        fill={solidColors.ballConnection}
      />

      {/* Neck gradient overlay */}
      <path
        d="M81.8498 205.75L114.82 296.62C121.47 314.95 138.6 327.38 158.09 328.02C180.01 328.74 199.6 314.42 205.56 293.31L230.88 203.63C239.36 173.58 217.17 143.63 185.95 142.99L127.67 141.8C94.1798 141.11 70.4298 174.26 81.8598 205.75H81.8498Z"
        fill={`url(#${gradients.ball2.id})`}
      />

      {/* Ball shadow */}
      <path
        opacity="0.15"
        d="M157.13 273.08C199.601 273.08 234.03 236.69 234.03 191.8C234.03 146.91 199.601 110.52 157.13 110.52C114.659 110.52 80.23 146.91 80.23 191.8C80.23 236.69 114.659 273.08 157.13 273.08Z"
        fill="black"
      />

      {/* Soccer ball - metallic gray surface */}
      <path
        d="M137.12 257.47C120.64 254.37 105.78 247.64 93.18 238.28C89.87 235.83 86.72 233.19 83.74 230.39C74.05 221.29 66.13 210.46 60.4 198.5C59.41 196.45 58.5 194.39 57.65 192.28C50.25 173.96 47.83 153.37 51.76 132.5C52.41 129.05 53.22 125.67 54.18 122.36C54.66 120.72 55.17 119.11 55.71 117.51C64.14 92.89 81.03 72.89 102.4 60.23C117.08 51.51 133.87 46.28 151.48 45.41C159.77 44.98 168.22 45.53 176.73 47.14C193.62 50.32 208.84 57.31 221.65 67.06C234.64 76.94 245.17 89.65 252.46 104.09C262.72 124.37 266.62 148.06 262.09 172.11C260.77 179.11 258.8 185.83 256.24 192.19C251.4 204.31 244.46 215.19 235.94 224.49C219.58 242.36 197.41 254.41 173.06 258.11H173.03C171.13 258.41 169.22 258.65 167.3 258.83C157.47 259.79 147.34 259.38 137.14 257.46L137.12 257.47Z"
        fill={`url(#${gradients.detail1.id})`}
      />

      {/* Soccer ball pentagon pattern details */}
      <path
        d="M167.28 258.84C169.21 258.66 171.11 258.42 173.01 258.12C163.29 257.82 156.72 249.7 156.72 249.7L176.97 233.46C205.63 228.35 219.88 218.62 219.88 218.62L235.91 224.5C244.43 215.2 251.36 204.32 256.21 192.2L254.81 165.02C254.81 165.02 256.61 145.3 256.16 137.57C255.71 129.96 252.43 112.93 252.33 112.4L252.45 104.1C245.16 89.6599 234.63 76.9499 221.64 67.0699L211.71 64.2499C197.65 57.7799 164.84 51.1499 164.84 51.1499C160.74 48.0299 156.19 46.2399 151.48 45.4199C133.87 46.2899 117.08 51.5199 102.4 60.2399L105.3 73.4199C91.3002 88.1099 80.0602 111.56 80.0602 111.56L59.8102 127.8C58.0502 126.82 56.5202 121.08 55.7102 117.51C55.1602 119.11 54.6602 120.72 54.1802 122.36C55.6402 127.58 58.5702 131.87 58.5702 131.87C58.5702 131.87 57.9302 140.35 58.2102 149C58.4902 157.65 60.6202 166.65 60.6202 166.65C58.7602 179.18 57.9802 187.34 57.6602 192.28C58.5002 194.39 59.4202 196.45 60.4102 198.49C58.9602 179.12 63.2802 172.88 63.2802 172.88L84.3202 177.8C98.7202 201.2 113.35 216.66 113.8 217.14L113.73 237.14C108.96 237.22 93.5202 237.03 86.9602 231.79C85.6902 230.78 84.6202 230.37 83.7402 230.39C86.7202 233.2 89.8702 235.83 93.1802 238.28C96.9102 239.32 103.79 240.42 116.17 240.4L151.77 250.68C155.28 256.98 166.88 258.79 167.27 258.84H167.28ZM248.89 114.67C257.35 132.45 252.61 164.12 252.61 164.12L233.97 176.85L207.18 151.75L204.21 116.8L229.02 96.6399L248.89 114.67ZM146.96 68.2899C152 61.1299 161.22 55.8899 165.17 53.8799C165.17 53.8799 198.81 61.8199 210.28 66.8499C221.75 71.8799 225.48 92.6399 225.48 92.6399L201.94 111.13L168.33 101.93L146.96 68.2999V68.2899ZM84.9402 113.59L86.2702 106.52C86.2702 106.52 87.5402 104.85 97.0202 88.8399C104.71 75.8499 111.62 72.9699 113.96 72.3399C114.31 72.2499 114.56 72.2099 114.69 72.1899H114.71C114.71 72.1899 114.76 72.1699 114.79 72.1699C127.13 69.7599 141.83 70.1699 141.83 70.1699L161.56 103.5L143.8 130.72L99.5102 137.66L84.9402 113.58V113.59ZM87.4202 176.47C92.6302 148.8 100.31 143.55 100.31 143.55L143.33 138.28L164.99 165.28L151.12 208.52L120.26 212.98C107.75 206.11 87.4302 176.47 87.4302 176.47H87.4202ZM156.58 209.56L170.18 168.11L204.46 156.02L231.08 182.05L222.58 211.97C222.58 211.97 218.77 216.98 204.29 222.85C189.81 228.72 175.28 229.8 175.28 229.8L156.58 209.56Z"
        fill={`url(#${gradients.detail2.id})`}
      />
    </svg>
  );
}
